import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  ArrowLeft,
  Phone,
  MapPin,
  Ruler,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Save,
  Shield,
  Upload,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import OnboardingTour from "@/components/OnboardingTour";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (user && user.user_type === "business" && user.has_seen_tour === false) {
      setShowTour(true);
    }
  }, [user]);

  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Verification state
  const [verification, setVerification] = useState(null);
  const [verSaving, setVerSaving] = useState(false);
  const [showVerForm, setShowVerForm] = useState(false);
  const [verForm, setVerForm] = useState({
    license_number: "",
    license_file_url: "",
    insurance_coi_url: "",
    ein: "",
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);

  const [formData, setFormData] = useState({
    business_name: "",
    description: "",
    phone: "",
    address: "",
    city: "",
    zip_code: "",
    service_categories: [],
    service_radius: 25,
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const fetchData = async () => {
    try {
      const categoriesRes = await axios.get(`${API}/categories?lang=${i18n.language?.startsWith('es') ? 'es' : 'en'}`);
      setCategories(categoriesRes.data);

      try {
        const profileRes = await axios.get(`${API}/business/profile`);
        setProfile(profileRes.data);
        setFormData({
          business_name: profileRes.data.business_name,
          description: profileRes.data.description,
          phone: profileRes.data.phone,
          address: profileRes.data.address,
          city: profileRes.data.city,
          zip_code: profileRes.data.zip_code,
          service_categories: profileRes.data.service_categories || [],
          service_radius: profileRes.data.service_radius || 25,
        });

        // Fetch verification status if business-approved
        if (profileRes.data.is_approved) {
          try {
            const verRes = await axios.get(`${API}/verification/me`);
            setVerification(verRes.data);
            setVerForm({
              license_number: verRes.data.license_number || "",
              license_file_url: verRes.data.license_file_url || "",
              insurance_coi_url: verRes.data.insurance_coi_url || "",
              ein: verRes.data.ein || "",
            });
          } catch { /* no verification record yet */ }
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setIsEditing(true);
        } else {
          throw error;
        }
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${API}/upload/verification`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.url;
  };

  const handleVerSubmit = async (e) => {
    e.preventDefault();
    setVerSaving(true);
    try {
      let licenseUrl = verForm.license_file_url;
      let insuranceUrl = verForm.insurance_coi_url;

      // Upload new files to Cloudinary
      if (licenseFile) {
        toast.info("Uploading license file...");
        licenseUrl = await uploadToCloudinary(licenseFile);
      }
      if (insuranceFile) {
        toast.info("Uploading insurance file...");
        insuranceUrl = await uploadToCloudinary(insuranceFile);
      }

      await axios.put(`${API}/verification/submit`, {
        license_number: verForm.license_number,
        license_file_url: licenseUrl,
        insurance_coi_url: insuranceUrl,
        ein: verForm.ein,
      });
      toast.success("Verification submitted for review!");
      setShowVerForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit verification");
    } finally {
      setVerSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData((prev) => {
      const categories = prev.service_categories.includes(categoryId)
        ? prev.service_categories.filter((id) => id !== categoryId)
        : [...prev.service_categories, categoryId];
      return { ...prev, service_categories: categories };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.business_name ||
      !formData.description ||
      !formData.phone ||
      !formData.city ||
      !formData.zip_code
    ) {
      toast.error(t("profile.fillRequired"));
      return;
    }

    if (formData.service_categories.length === 0) {
      toast.error(t("profile.selectOneCategory"));
      return;
    }

    setSaving(true);
    try {
      if (profile) {
        await axios.put(`${API}/business/profile`, formData);
        toast.success(t("profile.profileUpdated"));
      } else {
        await axios.post(`${API}/business/profile`, formData);
        toast.success(t("profile.profileCreated"));
      }
      fetchData();
      setIsEditing(false);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save profile";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const getApprovalStatus = () => {
    if (!profile) return null;
    
    const statusConfig = {
      pending: {
        icon: Clock,
        color: "text-yellow-600",
        bgColor: "bg-yellow-100",
        borderColor: "border-yellow-200",
        label: t("profile.pendingApproval"),
        description: t("profile.pendingApprovalDesc"),
      },
      approved: {
        icon: CheckCircle2,
        color: "text-green-600",
        bgColor: "bg-green-100",
        borderColor: "border-green-200",
        label: t("profile.approved"),
        description: t("profile.approvedDesc"),
      },
      rejected: {
        icon: XCircle,
        color: "text-red-600",
        bgColor: "bg-red-100",
        borderColor: "border-red-200",
        label: t("profile.rejected"),
        description: t("profile.rejectedDesc"),
      },
    };

    return statusConfig[profile.approval_status] || statusConfig.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const approvalStatus = getApprovalStatus();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/business"
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="font-bold text-[#0B1F3B]">MyProjectBidder</span>
              </div>
            </div>

            {profile && !isEditing && (
              <Button
                onClick={() => setIsEditing(true)}
                variant="outline"
                className="border-[#0B1F3B] text-[#0B1F3B]"
              >
                {t("profile.editProfile")}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="business-profile">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
            {profile ? t("profile.businessProfile") : t("profile.completeYourProfile")}
          </h1>
          <p className="text-slate-600 mt-2">
            {profile
              ? t("profile.manageInfo")
              : t("profile.setupProfile")}
          </p>
        </div>

        {/* Approval Status */}
        {approvalStatus && (
          <div
            className={`mb-6 p-4 ${approvalStatus.bgColor} border ${approvalStatus.borderColor} rounded-lg flex items-start gap-3`}
          >
            <approvalStatus.icon className={`w-5 h-5 ${approvalStatus.color} mt-0.5`} />
            <div>
              <p className={`font-semibold ${approvalStatus.color}`}>{approvalStatus.label}</p>
              <p className="text-sm text-slate-700">{approvalStatus.description}</p>
            </div>
          </div>
        )}

        {/* Verification Status Banner — only for admin-approved businesses */}
        {profile?.is_approved && verification && (
          <>
            {verification.verification_status === "approved" && (
              <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3" data-testid="ver-approved">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-emerald-700 flex items-center gap-1">{t("verification.verifiedContractor")} <CheckCircle2 className="w-4 h-4" /></p>
                  <p className="text-sm text-emerald-600">{t("verification.verifiedDesc")}</p>
                </div>
              </div>
            )}
            {verification.verification_status === "draft" && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between gap-4" data-testid="ver-draft">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-yellow-800">{t("verification.completeVerification")}</p>
                    <p className="text-sm text-yellow-700">{t("verification.uploadDocs")}</p>
                  </div>
                </div>
                <Button onClick={() => setShowVerForm(true)} className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold flex-shrink-0" data-testid="start-verification-btn">
                  {t("verification.startVerification")}
                </Button>
              </div>
            )}
            {(verification.verification_status === "submitted" || verification.verification_status === "pending_review") && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3" data-testid="ver-submitted">
                <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-blue-800">{t("verification.underReview")}</p>
                  <p className="text-sm text-blue-700">{t("verification.notifyIn24h")}</p>
                </div>
              </div>
            )}
            {verification.verification_status === "rejected" && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="ver-rejected">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-800">{t("verification.verificationRejected")}</p>
                      <p className="text-sm text-red-700 mt-1">{t("verification.reason")}: {verification.rejection_reason}</p>
                    </div>
                  </div>
                  <Button onClick={() => setShowVerForm(true)} className="bg-red-600 hover:bg-red-700 text-white font-bold flex-shrink-0" data-testid="resubmit-verification-btn">
                    Resubmit
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-[#0B1F3B] mb-4">{t("profile.businessInfo")}</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="business_name" className="text-[#0B1F3B] font-medium">
                    {t("profile.businessName")} *
                  </Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    placeholder="e.g., Smith Construction LLC"
                    className="mt-1.5 h-12"
                    data-testid="business-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-[#0B1F3B] font-medium">
                    {t("profile.businessDesc")} *
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your business, experience, and specialties..."
                    className="mt-1.5 min-h-[100px]"
                    data-testid="business-description-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#0B1F3B] font-medium">
                    {t("profile.phoneNumber")} *
                  </Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="pl-10 h-12"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-bold text-[#0B1F3B]">{t("profile.locationServiceArea")}</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="address" className="text-[#0B1F3B] font-medium">
                    Business Address
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main St"
                    className="mt-1.5 h-12"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="text-[#0B1F3B] font-medium">
                      City *
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="San Francisco"
                      className="mt-1.5 h-12"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code" className="text-[#0B1F3B] font-medium">
                      ZIP Code *
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      placeholder="94102"
                      className="mt-1.5 h-12"
                      data-testid="zip-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="service_radius" className="text-[#0B1F3B] font-medium">
                    Service Radius (miles)
                  </Label>
                  <div className="relative mt-1.5">
                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="service_radius"
                      name="service_radius"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.service_radius}
                      onChange={handleInputChange}
                      className="pl-10 h-12"
                      data-testid="radius-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Service Categories */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-[#0B1F3B] mb-2">{t("profile.serviceCategories")} *</h2>
              <p className="text-sm text-slate-500 mb-4">
                {t("profile.selectServices")}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {categories.map((category) => (
                  <label
                    key={category.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors duration-200 ${
                      formData.service_categories.includes(category.id)
                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`category-checkbox-${category.id}`}
                  >
                    <Checkbox
                      checked={formData.service_categories.includes(category.id)}
                      onCheckedChange={() => handleCategoryToggle(category.id)}
                    />
                    <span className="text-sm font-medium text-[#0B1F3B]">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              {profile && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  className="flex-1 h-12"
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={saving}
                className={`${profile ? "flex-1" : "w-full"} h-12 bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold`}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {profile ? t("profile.saveChanges") : t("profile.createProfile")}
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          /* View Mode */
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-[#0B1F3B] mb-4">Business Information</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Business Name</p>
                  <p className="font-medium text-[#0B1F3B]">{profile?.business_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Description</p>
                  <p className="text-slate-600">{profile?.description}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium text-[#0B1F3B]">{profile?.phone}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-[#0B1F3B] mb-4">Location & Service Area</h2>
              <div className="space-y-3">
                {profile?.address && (
                  <div>
                    <p className="text-sm text-slate-500">Address</p>
                    <p className="font-medium text-[#0B1F3B]">{profile.address}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">City & ZIP</p>
                  <p className="font-medium text-[#0B1F3B]">
                    {profile?.city}, {profile?.zip_code}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Service Radius</p>
                  <p className="font-medium text-[#0B1F3B]">{profile?.service_radius} miles</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-[#0B1F3B] mb-4">Services Offered</h2>
              <div className="flex flex-wrap gap-2">
                {profile?.service_categories?.map((catId) => {
                  const cat = categories.find((c) => c.id === catId);
                  return cat ? (
                    <Badge
                      key={catId}
                      className="bg-[#D4AF37]/10 text-[#0B1F3B] border border-[#D4AF37]"
                    >
                      {cat.name}
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>

            {/* Verification Form — only for admin-approved, not yet verification-approved */}
            {profile?.is_approved && showVerForm && (
              <form onSubmit={handleVerSubmit} className="bg-white rounded-xl border-2 border-[#0B1F3B] p-6" data-testid="verification-form">
                <h2 className="font-bold text-[#0B1F3B] mb-1 flex items-center gap-2">
                  <Shield className="w-5 h-5" /> {t("verification.title")}
                </h2>
                <p className="text-sm text-slate-500 mb-5">{t("verification.subtitle")}</p>

                <div className="space-y-5">
                  {/* License Number */}
                  <div>
                    <Label htmlFor="license_number" className="text-[#0B1F3B] font-medium">{t("verification.licenseNumber")}</Label>
                    <Input
                      id="license_number"
                      value={verForm.license_number}
                      onChange={(e) => setVerForm(p => ({ ...p, license_number: e.target.value }))}
                      placeholder="e.g., LIC-2026-12345"
                      className="mt-1.5 h-12"
                      data-testid="ver-license-number"
                    />
                  </div>

                  {/* License File Upload */}
                  <div>
                    <Label className="text-[#0B1F3B] font-medium">{t("verification.licenseFile")}</Label>
                    <div className="mt-1.5">
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#D4AF37] hover:bg-slate-50 transition-colors">
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-500">{licenseFile ? licenseFile.name : verForm.license_file_url ? "File uploaded — click to replace" : "Click to upload"}</span>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          className="hidden"
                          onChange={(e) => { if (e.target.files[0]) setLicenseFile(e.target.files[0]); }}
                          data-testid="ver-license-file"
                        />
                      </label>
                      {(licenseFile || verForm.license_file_url) && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                          <FileText className="w-4 h-4" />
                          <span>{licenseFile ? licenseFile.name : "Previously uploaded file"}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Insurance COI Upload */}
                  <div>
                    <Label className="text-[#0B1F3B] font-medium">{t("verification.insuranceCOI")}</Label>
                    <div className="mt-1.5">
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#D4AF37] hover:bg-slate-50 transition-colors">
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-500">{insuranceFile ? insuranceFile.name : verForm.insurance_coi_url ? "File uploaded — click to replace" : "Click to upload"}</span>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          className="hidden"
                          onChange={(e) => { if (e.target.files[0]) setInsuranceFile(e.target.files[0]); }}
                          data-testid="ver-insurance-file"
                        />
                      </label>
                      {(insuranceFile || verForm.insurance_coi_url) && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                          <FileText className="w-4 h-4" />
                          <span>{insuranceFile ? insuranceFile.name : "Previously uploaded file"}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* EIN */}
                  <div>
                    <Label htmlFor="ein" className="text-[#0B1F3B] font-medium">{t("verification.einLabel")}</Label>
                    <Input
                      id="ein"
                      value={verForm.ein}
                      onChange={(e) => setVerForm(p => ({ ...p, ein: e.target.value }))}
                      placeholder="e.g., 12-3456789"
                      className="mt-1.5 h-12"
                      data-testid="ver-ein"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 mt-6">
                  <Button type="button" variant="outline" onClick={() => setShowVerForm(false)} className="flex-1 h-12">{t("common.cancel")}</Button>
                  <Button type="submit" disabled={verSaving} className="flex-1 h-12 bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold" data-testid="submit-verification-btn">
                    {verSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("verification.submitting")}</> : <><Shield className="w-4 h-4 mr-2" /> {t("verification.submitForReview")}</>}
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}
      </main>
      {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}
    </div>
  );
}
